import { ref, onBeforeUnmount } from 'vue';
import AdvisorAPI from 'dashboard/api/teletel/advisor';

// Voice for the manager advisor. Recording goes to the company's AI provider
// for transcription; when that provider has no audio endpoint (the server
// answers 422 with a stable code) the page switches, once, to the browser's
// own recognition and synthesis and stays there for the session.
const UNAVAILABLE = 'TELETEL_ADVISOR_VOICE_UNAVAILABLE';
const MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

const speechRecognition = () =>
  window.SpeechRecognition || window.webkitSpeechRecognition || null;

const pickMimeType = () =>
  MIME_TYPES.find(type => window.MediaRecorder?.isTypeSupported?.(type)) || '';

const isUnavailable = e => e?.response?.data?.code === UNAVAILABLE;

export function useAdvisorVoice({ locale, onText, onError }) {
  const isRecording = ref(false);
  const isTranscribing = ref(false);
  const speakingId = ref(null);
  const usingBrowser = ref(false);

  let recorder = null;
  let stream = null;
  let chunks = [];
  let recognition = null;
  let player = null;

  const canRecord = () =>
    Boolean(
      (navigator.mediaDevices?.getUserMedia && window.MediaRecorder) ||
        speechRecognition()
    );

  const lang = () => (locale() === 'ar' ? 'ar-SA' : 'en-US');

  const stopStream = () => {
    if (stream) stream.getTracks().forEach(track => track.stop());
    stream = null;
  };

  // The browser's recognizer listens live, so it replaces the recorder
  // rather than reading the recording back.
  const startBrowserRecognition = () => {
    const Recognition = speechRecognition();
    if (!Recognition) {
      onError('NO_VOICE');
      return;
    }
    recognition = new Recognition();
    recognition.lang = lang();
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = event => {
      const text = Array.from(event.results)
        .map(result => result[0]?.transcript || '')
        .join(' ')
        .trim();
      if (text) onText(text);
    };
    recognition.onerror = () => onError('VOICE_FAILED');
    recognition.onend = () => {
      isRecording.value = false;
      recognition = null;
    };
    isRecording.value = true;
    recognition.start();
  };

  const upload = async blob => {
    isTranscribing.value = true;
    try {
      const { data } = await AdvisorAPI.transcribe(blob);
      const text = (data?.payload?.text || '').trim();
      if (text) onText(text);
      else onError('VOICE_EMPTY');
    } catch (e) {
      if (isUnavailable(e) && speechRecognition()) {
        usingBrowser.value = true;
        onError('VOICE_BROWSER_FALLBACK');
      } else {
        onError(e?.response?.data?.error || 'VOICE_FAILED');
      }
    } finally {
      isTranscribing.value = false;
    }
  };

  const startRecorder = async () => {
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      onError('MIC_DENIED');
      return;
    }
    chunks = [];
    const mimeType = pickMimeType();
    recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    recorder.ondataavailable = event => {
      if (event.data?.size) chunks.push(event.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunks, {
        type: recorder.mimeType || 'audio/webm',
      });
      stopStream();
      recorder = null;
      isRecording.value = false;
      if (blob.size > 0) upload(blob);
    };
    recorder.start();
    isRecording.value = true;
  };

  const start = async () => {
    if (isRecording.value || isTranscribing.value) return;
    if (usingBrowser.value || !window.MediaRecorder) {
      startBrowserRecognition();
      return;
    }
    await startRecorder();
  };

  const stop = () => {
    if (recognition) {
      recognition.stop();
      return;
    }
    if (recorder && recorder.state !== 'inactive') recorder.stop();
  };

  const toggle = () => (isRecording.value ? stop() : start());

  const stopSpeaking = () => {
    if (player) {
      player.pause();
      player = null;
    }
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    speakingId.value = null;
  };

  const speakInBrowser = (id, text) => {
    if (!window.speechSynthesis) {
      onError('NO_VOICE');
      return;
    }
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang();
    utterance.onend = () => {
      if (speakingId.value === id) speakingId.value = null;
    };
    speakingId.value = id;
    window.speechSynthesis.speak(utterance);
  };

  const speak = async (id, text) => {
    if (speakingId.value === id) {
      stopSpeaking();
      return;
    }
    stopSpeaking();
    if (usingBrowser.value) {
      speakInBrowser(id, text);
      return;
    }
    speakingId.value = id;
    try {
      const { data } = await AdvisorAPI.speak(text);
      if (speakingId.value !== id) return;
      player = new Audio(URL.createObjectURL(data));
      player.onended = () => {
        if (speakingId.value === id) speakingId.value = null;
      };
      await player.play();
    } catch (e) {
      speakingId.value = null;
      // The speech call asks for a blob, so a 422 body is not parsed JSON;
      // the status alone says the provider has no audio.
      if (e?.response?.status === 422) {
        usingBrowser.value = true;
        speakInBrowser(id, text);
      } else {
        onError('VOICE_FAILED');
      }
    }
  };

  onBeforeUnmount(() => {
    stop();
    stopStream();
    stopSpeaking();
  });

  return {
    canRecord,
    isRecording,
    isTranscribing,
    speakingId,
    usingBrowser,
    toggle,
    speak,
    stopSpeaking,
  };
}
